import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";

const STEPS = [
  { n: "01", title: "Discovery", desc: "Brief, site context, constraints and ambitions — aligned before a single line is drawn." },
  { n: "02", title: "Concept & Design", desc: "Architectural vision developed in parallel with structural and MEP logic from day one." },
  { n: "03", title: "BIM Modeling", desc: "Design converted to data-rich 3D models at the required LOD — 100 through 500." },
  { n: "04", title: "Coordination", desc: "Architecture + structure + MEP clash-detected and resolved into a zero-clash model." },
  { n: "05", title: "Review & Optimize", desc: "4D schedules, 5D cost and value engineering refine the outcome before build." },
  { n: "06", title: "Handover", desc: "Coordinated drawings, models, COBie and FM-ready asset data — delivered complete." },
];

export default function Workflow() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.55"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 22 });

  return (
    <section id="process" className="py-28 lg:py-36 border-b border-white/10" data-testid="workflow-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="font-mono-tech text-xs tracking-[0.35em] uppercase text-neutral-500 mb-4">
          03 / Process
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="font-heading font-bold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl mb-20 max-w-2xl">
          From brief to handover — one flowing pipeline
        </motion.h2>

        <div ref={ref} className="relative pl-10 md:pl-16 max-w-3xl">
          <div className="absolute left-2 md:left-4 top-0 bottom-0 w-px bg-white/10" />
          <motion.div className="absolute left-2 md:left-4 top-0 bottom-0 w-px bg-white origin-top" style={{ scaleY }} />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="relative pb-14 last:pb-0"
              data-testid={`workflow-step-${s.n}`}
            >
              <motion.span
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.45, delay: i * 0.08 + 0.15 }}
                className="absolute -left-10 md:-left-16 top-1.5 translate-x-[5px] md:translate-x-[9px] w-2 h-2 bg-white rotate-45"
              />
              <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-neutral-600">{s.n}</span>
              <h3 className="font-heading font-bold uppercase tracking-tight text-xl lg:text-2xl mt-1 mb-2">{s.title}</h3>
              <p className="text-sm text-neutral-400 font-light leading-relaxed max-w-lg">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
